import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { api } from '../auth/api';
import { useAuth } from '../auth/AuthContext';
import {
  replaceAllProblems, upsertProblem, removeProblemById, findProblemById,
  type Problem,
} from '../data/problems';

// The bank's source of truth is the DynamoDB store. The bundled JSON only seeds
// the loader for an instant first paint / offline fallback; on boot we fetch the
// live set from `GET /api/problems/all` and swap it in. Admin create/edit/delete
// go through `saveProblem` / `deleteProblem`, which hit the admin API and patch
// the in-memory store in place so the change shows everywhere immediately.
//
// `version` increments on every store mutation. Components that render bank data
// don't subscribe to individual problems — they read the loader's synchronous
// accessors — so they consume `version` (via `useProblemStoreVersion`) purely to
// re-render when the bank changes.

interface ProblemStoreValue {
  version: number;
  /** True once the live `/all` fetch has resolved (success or fail). */
  hydrated: boolean;
  /** Create (POST) or replace (PUT) a problem. Returns the saved body, or throws
   *  with a message suitable for surfacing in the editor. */
  saveProblem: (body: Problem) => Promise<Problem>;
  /** Delete a problem by id. Idempotent. */
  deleteProblem: (id: string) => Promise<void>;
}

const ProblemStoreContext = createContext<ProblemStoreValue | null>(null);

export const ProblemStoreProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state } = useAuth();
  const token = state.kind === 'authenticated' ? state.idToken : undefined;
  const [version, setVersion] = useState(0);
  const [hydrated, setHydrated] = useState(false);
  const bump = useCallback(() => setVersion(v => v + 1), []);

  // Hydrate once on boot. The endpoint is public, so no token is required; an
  // anonymous reader gets the same live bank as a signed-in user.
  const didHydrate = useRef(false);
  useEffect(() => {
    if (didHydrate.current) return;
    didHydrate.current = true;
    let cancelled = false;
    (async () => {
      try {
        const res = await api('/api/problems/all');
        if (!cancelled && res.ok) {
          const data = await res.json();
          if (Array.isArray(data.problems)) {
            replaceAllProblems(data.problems as Problem[]);
            bump();
          }
        }
      } catch {
        // Network/parse failure → keep the bundled seed (offline-friendly).
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => { cancelled = true; };
  }, [bump]);

  const saveProblem = useCallback(async (body: Problem): Promise<Problem> => {
    const id = body.id;
    if (!id) throw new Error('Problem is missing an id.');
    // New id → POST (server 409s on a collision); existing → PUT (replace).
    const exists = !!findProblemById(id);
    const res = exists
      ? await api(`/api/problems/${encodeURIComponent(id)}`, { method: 'PUT', token, body: { body } })
      : await api('/api/problems', { method: 'POST', token, body: { body } });
    if (!res.ok) {
      const detail = await res.json().catch(() => ({}));
      throw new Error(detail.detail || `Save failed (${res.status})`);
    }
    const saved = await res.json();
    const savedBody = (saved.body as Problem) ?? body;
    upsertProblem(savedBody);
    bump();
    return savedBody;
  }, [token, bump]);

  const deleteProblem = useCallback(async (id: string): Promise<void> => {
    const res = await api(`/api/problems/${encodeURIComponent(id)}`, { method: 'DELETE', token });
    if (!res.ok && res.status !== 404) {
      const detail = await res.json().catch(() => ({}));
      throw new Error(detail.detail || `Delete failed (${res.status})`);
    }
    removeProblemById(id);
    bump();
  }, [token, bump]);

  const value = useMemo<ProblemStoreValue>(
    () => ({ version, hydrated, saveProblem, deleteProblem }),
    [version, hydrated, saveProblem, deleteProblem],
  );

  return (
    <ProblemStoreContext.Provider value={value}>{children}</ProblemStoreContext.Provider>
  );
};

export function useProblemStore(): ProblemStoreValue {
  const ctx = useContext(ProblemStoreContext);
  if (!ctx) throw new Error('useProblemStore must be used within ProblemStoreProvider');
  return ctx;
}

/** Subscribe a component to bank changes — re-renders when a problem is added,
 *  edited, deleted, or the live set is hydrated. */
export function useProblemStoreVersion(): number {
  return useProblemStore().version;
}
